// ═══════════════════════════════════════════════════════════════════════
//   FINANCEIRO DO ALUNO organizado por CONTRATO
//   Contrato → Plano(s) contratado(s) → Títulos reais (DNA Pay)
// ═══════════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useCallback } from 'react';
import {
  Loader2, Wallet, Receipt, Pencil, Trash2, PlusCircle, ExternalLink,
  AlertTriangle, RefreshCw, ChevronRight, FileDown, CheckCircle2, Wrench,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API_URL = window.location.origin;
const H = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` });
const money = (v) => (Number(v || 0)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const fmtBR = (iso) => (iso ? iso.split('-').reverse().join('/') : '—');
// Prévia com a mesma regra do motor de títulos: 1ª data = ano/mês/dia do plano; mensal soma meses.
const previewDates = (year, month, day, n, monthly) => {
  const out = [];
  for (let i = 0; i < Math.max(1, n || 1); i += 1) {
    const total = Number(year) * 12 + (Number(month) - 1) + (monthly ? i : 0);
    const y = Math.floor(total / 12); const m = (total % 12) + 1;
    const last = new Date(y, m, 0).getDate();
    out.push(`${y}-${String(m).padStart(2, '0')}-${String(Math.min(Math.max(1, Number(day) || 10), last)).padStart(2, '0')}`);
  }
  return out;
};

const KPI = ({ label, value, tone = 'slate', testid }) => (
  <div className={`rounded-xl border px-3 py-2 bg-white ${tone === 'emerald' ? 'border-emerald-200' : tone === 'amber' ? 'border-amber-200' : tone === 'rose' ? 'border-rose-200' : 'border-slate-200'}`} data-testid={testid}>
    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{label}</p>
    <p className={`text-sm font-bold ${tone === 'emerald' ? 'text-emerald-700' : tone === 'amber' ? 'text-amber-700' : tone === 'rose' ? 'text-rose-700' : 'text-slate-900'}`}>{value}</p>
  </div>
);

// ─── Diálogo editar/adicionar plano do contrato ───────────────────────
const PlanoDialog = ({ open, onOpenChange, contract, item, coursePlans, onDone }) => {
  const isEdit = !!item;
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAsk(false);
    setF(isEdit
      ? { label: item.label, amount: item.valor, installments: item.parcelas,
          first_month: item.mes_inicial || 1, first_year: item.ano_inicial || contract?.academic_year,
          due_day: item.dia_vencimento || 10,
          discount_type: item.desconto_tipo || 'none', discount_value: item.desconto_valor || 0,
          notes: item.observacao || '' }
      : { category: '', label: '', amount: '', installments: 12, first_month: 1, first_year: contract?.academic_year, due_day: 10,
          discount_type: 'none', discount_value: 0, notes: '', course_financial_plan_id: '' });
  }, [open, isEdit, item, contract]);

  const save = async (updateTitles) => {
    setBusy(true);
    try {
      const url = isEdit
        ? `${API_URL}/api/student-contracts/contract/${contract.contract_id}/plan-item/${encodeURIComponent(item.key)}`
        : `${API_URL}/api/student-contracts/contract/${contract.contract_id}/plan-item`;
      // Na edição, só envia mês/ano/dia se mudaram (evita recalcular datas sem necessidade).
      const changed = (k, orig) => !isEdit || Number(f[k]) !== Number(orig);
      const body = {
        ...f,
        amount: f.amount === '' || f.amount === null ? null : Number(String(f.amount).replace(',', '.')),
        installments: f.installments ? Number(f.installments) : null,
        first_month: f.first_month && changed('first_month', item?.mes_inicial) ? Number(f.first_month) : null,
        first_year: f.first_year && changed('first_year', item?.ano_inicial) ? Number(f.first_year) : null,
        due_day: f.due_day && changed('due_day', item?.dia_vencimento) ? Number(f.due_day) : null,
        discount_value: Number(f.discount_value || 0),
        update_titles: !!updateTitles,
      };
      const res = await fetch(url, { method: isEdit ? 'PATCH' : 'POST', headers: H(), body: JSON.stringify(body) });
      const json = await res.json();
      if (!res.ok) throw new Error(typeof json.detail === 'string' ? json.detail : 'Falha ao salvar o plano');
      toast.success(isEdit
        ? `Plano atualizado${json.titulos_atualizados ? ` · ${json.titulos_atualizados} título(s) pendente(s) atualizado(s)` : ' (títulos não alterados)'}${json.datas_recalculadas ? ` · ${json.datas_recalculadas} vencimento(s) recalculado(s)` : ''}${json.preservados_boleto_remessa ? ` · ${json.preservados_boleto_remessa} com boleto/remessa mantido(s)` : ''}`
        : 'Plano adicionado ao contrato — títulos continuam sendo gerados no DNA Pay');
      onOpenChange(false);
      onDone?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const temTitulos = isEdit && (item?.titulos?.quantidade || 0) > 0;
  const nParc = Number(f.installments) || 1;
  const monthly = isEdit ? !!item?.mensal : nParc > 1;
  const prev = f.first_year && f.first_month ? previewDates(f.first_year, f.first_month, f.due_day, nParc, monthly) : [];
  const baseYear = Number(item?.ano_inicial || contract?.academic_year) || null;
  const anos = baseYear ? Array.from(new Set([baseYear - 1, baseYear, baseYear + 1, baseYear + 2, Number(f.first_year) || baseYear])).sort() : [];

  if (!contract) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="plano-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Wallet className="w-4 h-4 text-violet-600" />
            {isEdit ? `Editar plano · ${item?.label}` : 'Adicionar plano ao contrato'} · {contract?.academic_year}
          </DialogTitle>
        </DialogHeader>
        {ask && item ? (
          <div className="space-y-3" data-testid="plano-ask-titulos">
            <p className="text-sm text-slate-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              Este plano possui <b>{item.titulos?.quantidade} título(s) gerado(s)</b> ({item.titulos?.pagos} pago(s)).
              Deseja alterar apenas o plano futuro ou também atualizar os títulos pendentes
              (valor e vencimento recalculados pela parcela)? Títulos pagos/baixados nunca são alterados;
              títulos com boleto/remessa registrados mantêm a data do banco.
            </p>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="ghost" onClick={() => setAsk(false)} disabled={busy}>Cancelar</Button>
              <Button variant="outline" onClick={() => save(false)} disabled={busy} data-testid="plano-somente">
                Alterar somente plano
              </Button>
              <Button onClick={() => save(true)} disabled={busy} className="bg-violet-600 hover:bg-violet-700" data-testid="plano-com-titulos">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Atualizar títulos pendentes'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {!isEdit && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Plano disponível do curso</Label>
                  <Select value={f.course_financial_plan_id || ''}
                          onValueChange={v => {
                            const p = (coursePlans || []).find(x => x.id === v) || {};
                            setF(s => ({ ...s, course_financial_plan_id: v, category: p.category || s.category,
                              label: p.name || s.label, amount: p.installment_value ?? s.amount,
                              installments: p.num_installments || s.installments, due_day: p.due_day || s.due_day }));
                          }}>
                    <SelectTrigger data-testid="plano-curso-select"><SelectValue placeholder="Copiar de um plano do curso" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {(coursePlans || []).map(p => <SelectItem key={p.id} value={p.id}>{p.name} · {money(p.installment_value)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Categoria *</Label>
                  <Select value={f.category || ''} onValueChange={v => setF(s => ({ ...s, category: v }))}>
                    <SelectTrigger data-testid="plano-categoria"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      {['mensalidade', 'matricula', 'material', 'bilingue', 'integral', 'almoco', 'anuidade']
                        .map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div className="col-span-2 sm:col-span-3">
                <Label className="text-xs font-semibold text-slate-700">Nome do serviço</Label>
                <Input value={f.label || ''} onChange={e => setF(s => ({ ...s, label: e.target.value }))} data-testid="plano-label" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700">Valor</Label>
                <Input value={f.amount ?? ''} onChange={e => setF(s => ({ ...s, amount: e.target.value }))} data-testid="plano-valor" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700">Parcelas</Label>
                <Input type="number" value={f.installments ?? ''} onChange={e => setF(s => ({ ...s, installments: e.target.value }))} data-testid="plano-parcelas" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700">Mês inicial</Label>
                <Select value={String(f.first_month || 1)} onValueChange={v => setF(s => ({ ...s, first_month: v }))}>
                  <SelectTrigger data-testid="plano-mes"><SelectValue /></SelectTrigger>
                  <SelectContent>{MESES.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700">Ano inicial</Label>
                <Select value={String(f.first_year || '')} onValueChange={v => setF(s => ({ ...s, first_year: v }))}>
                  <SelectTrigger data-testid="plano-ano"><SelectValue placeholder="Ano" /></SelectTrigger>
                  <SelectContent>{anos.map(a => <SelectItem key={a} value={String(a)}>{a}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700">Dia de vencimento</Label>
                <Input type="number" min={1} max={31} value={f.due_day ?? ''} onChange={e => setF(s => ({ ...s, due_day: e.target.value }))} data-testid="plano-venc" />
              </div>
              <div className="col-span-2 sm:col-span-3 rounded-lg border border-violet-100 bg-violet-50 px-3 py-2 text-[12px] text-violet-900" data-testid="plano-previa">
                <b>Prévia:</b>{' '}
                {prev.length ? `${prev.length} parcela${prev.length > 1 ? 's' : ''}: ${fmtBR(prev[0])}${prev.length > 1 ? ` → ${fmtBR(prev[prev.length - 1])}` : ''}` : 'informe mês e ano iniciais'}
                {isEdit && item?.primeiro_vencimento && (
                  <span className="block text-violet-700/70" data-testid="plano-previa-atual">
                    Atual: {fmtBR(item.primeiro_vencimento)}{item.ultimo_vencimento && item.ultimo_vencimento !== item.primeiro_vencimento ? ` → ${fmtBR(item.ultimo_vencimento)}` : ''}
                  </span>
                )}
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700">Desconto</Label>
                <Select value={f.discount_type || 'none'} onValueChange={v => setF(s => ({ ...s, discount_type: v }))}>
                  <SelectTrigger data-testid="plano-desc-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem desconto</SelectItem>
                    <SelectItem value="percent">Percentual (%)</SelectItem>
                    <SelectItem value="fixed">Valor fixo (R$)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700">Desconto (valor)</Label>
                <Input value={f.discount_value ?? 0} onChange={e => setF(s => ({ ...s, discount_value: e.target.value }))} data-testid="plano-desc-valor" />
              </div>
              <div className="col-span-2 sm:col-span-3">
                <Label className="text-xs font-semibold text-slate-700">Observação</Label>
                <Input value={f.notes || ''} onChange={e => setF(s => ({ ...s, notes: e.target.value }))} data-testid="plano-obs" />
              </div>
            </div>
            <p className="text-[11px] text-slate-400">
              Criar ou editar plano <b>não gera títulos</b> — a geração continua no fluxo do DNA Pay.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancelar</Button>
              <Button onClick={() => (temTitulos ? setAsk(true) : save(false))} disabled={busy}
                      className="bg-violet-600 hover:bg-violet-700" data-testid="plano-salvar">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar plano'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─── Tabela de títulos reais ──────────────────────────────────────────
const TitulosDialog = ({ open, onOpenChange, contract, item }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open || !contract) return;
    (async () => {
      setLoading(true);
      try {
        const q = item ? `?item_key=${encodeURIComponent(item.key)}` : '';
        const res = await fetch(`${API_URL}/api/student-contracts/contract/${contract.contract_id}/titles${q}`, { headers: H() });
        const json = await res.json();
        setRows(json.titulos || []);
      } catch { toast.error('Falha ao carregar títulos'); } finally { setLoading(false); }
    })();
  }, [open, contract, item]);

  if (!contract) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="titulos-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Receipt className="w-4 h-4 text-violet-600" />
            Títulos reais · Contrato {contract?.contract_number} {item ? `· ${item.label}` : ''}
          </DialogTitle>
        </DialogHeader>
        {loading ? <p className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Carregando…</p> : (
          <div className="max-h-[60vh] overflow-auto">
            <table className="w-full text-[12px]">
              <thead className="text-[10px] uppercase text-slate-400 border-b">
                <tr><th className="text-left py-1">Parcela</th><th className="text-left">Categoria</th>
                  <th className="text-left">Vencimento</th><th className="text-right">Valor</th>
                  <th className="text-left pl-3">Status</th><th className="text-left">Origem</th><th /></tr>
              </thead>
              <tbody>
                {rows.map((t, i) => (
                  <tr key={t.id} className="border-b border-slate-100" data-testid={`titulo-row-${i}`}>
                    <td className="py-1.5">{t.referencia || i + 1}</td>
                    <td>{t.categoria || '—'}</td>
                    <td>{t.vencimento ? t.vencimento.split('-').reverse().join('/') : '—'}</td>
                    <td className="text-right font-semibold">{money(t.valor)}</td>
                    <td className="pl-3">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${
                        t.status === 'pago' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : ['vencido', 'overdue'].includes(t.status) ? 'bg-rose-50 text-rose-700 border-rose-200'
                          : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{t.status}</span>
                    </td>
                    <td className="text-slate-400">{t.origem}</td>
                    <td className="text-right">
                      <a href={`/v2/financeiro/dnapay/titulos/${t.id}`} target="_blank" rel="noreferrer"
                         className="text-violet-600 hover:underline inline-flex items-center gap-1"
                         data-testid={`titulo-abrir-${i}`}>
                        Abrir <ExternalLink className="w-3 h-3" />
                      </a>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={7} className="py-4 text-center text-slate-400">Nenhum título gerado ainda.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─── Bloco principal ─────────────────────────────────────────────────
export const FinanceiroContratosBlock = ({ studentId, focusContractId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planDlg, setPlanDlg] = useState(null);   // {contract, item, coursePlans}
  const [titDlg, setTitDlg] = useState(null);     // {contract, item}

  const load = useCallback(async (refresh) => {
    if (!studentId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/student-contracts/${studentId}/financeiro`, { headers: H() });
      if (!res.ok) throw new Error('Não foi possível carregar o financeiro do aluno');
      setData(await res.json());
      if (refresh) toast.success('Financeiro atualizado');
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!focusContractId || !data) return;
    const cid = String(focusContractId).split('#')[0];
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="fin-contrato-${cid}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', 'ring-violet-400', 'ring-offset-2');
      setTimeout(() => el.classList.remove('ring-2', 'ring-violet-400', 'ring-offset-2'), 3500);
    }, 450);
    return () => clearTimeout(t);
  }, [focusContractId, data]);

  const baixarExtrato = async (year) => {
    const id = toast.loading('Gerando extrato em PDF…');
    try {
      const q = year ? `?year=${year}` : '';
      const res = await fetch(`${API_URL}/api/student-contracts/${studentId}/extrato${q}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (!res.ok) throw new Error('Não foi possível gerar o extrato');
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url; a.download = `extrato-${year || 'completo'}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 8000);
      toast.success('Extrato gerado — pronto para enviar à família', { id });
    } catch (e) { toast.error(e.message, { id }); }
  };

  const reconciliar = async (bloco) => {
    const id = toast.loading('Conferindo plano × títulos…');
    try {
      const res = await fetch(`${API_URL}/api/student-contracts/contract/${bloco.contract_id}/reconcile`,
        { method: 'POST', headers: H() });
      const json = await res.json();
      if (!res.ok) throw new Error(typeof json.detail === 'string' ? json.detail : 'Falha na reconciliação');
      toast.success(
        json.criados
          ? `${json.criados} parcela(s) faltante(s) criada(s) · ${json.existentes_depois} de ${json.esperados} títulos`
          : `Plano já estava conciliado · ${json.existentes_depois} de ${json.esperados} títulos`,
        { id, description: json.protegidos_pagos ? `${json.protegidos_pagos} título(s) pago(s) preservado(s)` : undefined });
      load();
    } catch (e) { toast.error(e.message, { id }); }
  };

  const excluir = async (bloco, item) => {    const pagos = item.titulos?.pagos || 0;
    const qtd = item.titulos?.quantidade || 0;
    if (pagos > 0) {
      if (!window.confirm(`Existem ${pagos} título(s) pago(s) vinculados. O histórico financeiro deve ser preservado. Deseja ENCERRAR/INATIVAR o plano mantendo o histórico?`)) return;
    } else if (qtd > 0) {
      if (!window.confirm(`Este plano possui ${qtd} título(s) pendente(s) vinculados. Excluir o plano mesmo assim?`)) return;
    } else if (!window.confirm(`Excluir o plano "${item.label}" deste contrato?`)) return;
    try {
      const mode = pagos > 0 ? 'inactivate' : 'delete';
      const res = await fetch(`${API_URL}/api/student-contracts/contract/${bloco.contract_id}/plan-item/${encodeURIComponent(item.key)}?mode=${mode}`,
        { method: 'DELETE', headers: H() });
      const json = await res.json();
      if (!res.ok) throw new Error(typeof json.detail === 'string' ? json.detail : 'Falha ao excluir');
      toast.success(mode === 'inactivate' ? 'Plano encerrado — histórico preservado' : 'Plano excluído');
      load();
    } catch (e) { toast.error(e.message); }
  };

  if (loading && !data) {
    return <div className="flex items-center gap-2 text-sm text-slate-500 py-6" data-testid="fin-loading">
      <Loader2 className="w-4 h-4 animate-spin" /> Carregando financeiro do aluno…</div>;
  }
  const ind = data?.indicadores || {};

  return (
    <div className="space-y-4" data-testid="financeiro-contratos-block">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-slate-500">
          Contrato → plano contratado → títulos reais (DNA Pay). Criar ou editar plano não gera títulos.
        </p>
        <button type="button" onClick={() => load(true)}
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-violet-700 hover:text-violet-900"
                data-testid="fin-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Atualizar
        </button>
        <button type="button" onClick={() => baixarExtrato(null)}
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-lg border border-violet-300 text-violet-700 hover:bg-violet-50"
                data-testid="fin-extrato-completo">
          <FileDown className="w-3.5 h-3.5" /> Extrato completo (PDF)
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <KPI label="Contratos" value={ind.contratos ?? 0} testid="fin-kpi-contratos" />
        <KPI label="Planos ativos" value={ind.planos_ativos ?? 0} testid="fin-kpi-planos" />
        <KPI label="Títulos" value={ind.titulos ?? 0} testid="fin-kpi-titulos" />
        <KPI label="Pago" value={money(ind.pago)} tone="emerald" testid="fin-kpi-pago" />
        <KPI label="Pendente" value={money(ind.pendente)} tone="amber" testid="fin-kpi-pendente" />
        <KPI label="Vencido" value={money(ind.vencido)} tone="rose" testid="fin-kpi-vencido" />
      </div>

      <p className="text-[11px] font-bold uppercase tracking-wider text-violet-700">
        Planos contratados pelo aluno
      </p>

      {(data?.blocos || []).length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5 text-center text-sm text-slate-500" data-testid="fin-empty">
          Nenhum contrato financeiro encontrado para este aluno.
        </div>
      )}

      {(data?.blocos || []).map(b => (
        <div key={b.contract_id} className="rounded-2xl border border-slate-200 bg-white overflow-hidden transition-shadow"
             data-testid={`fin-contrato-${b.contract_id}`}>
          <div className="flex items-center gap-3 flex-wrap px-4 py-3 bg-slate-50/70 border-b border-slate-100">
            <div className="flex-1 min-w-[230px]">
              <p className="text-sm font-bold text-slate-900 flex items-center gap-2 flex-wrap">
                <span className="whitespace-nowrap">Contrato {b.contract_number} — {b.academic_year}</span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-white text-slate-600 border-slate-200 whitespace-nowrap">
                  {b.status}
                </span>
              </p>
              <p className="text-xs text-slate-500">{b.class_name || 'Turma não definida'}</p>
            </div>
            {b.previstas_total > 0 && (
              b.consistente ? (
                <span className="inline-flex items-center gap-1 text-[10.5px] font-bold px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
                      data-testid={`fin-consistente-${b.academic_year}`}>
                  <CheckCircle2 className="w-3 h-3" /> Plano conciliado · {b.previstas_total} previstas · {b.gerados_total} títulos
                </span>
              ) : (
                <span className="inline-flex items-center gap-2 text-[10.5px] font-bold px-2 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200"
                      data-testid={`fin-divergencia-${b.academic_year}`}>
                  <AlertTriangle className="w-3 h-3" /> Divergência: {b.gerados_total} de {b.previstas_total} títulos gerados
                  <button type="button" onClick={(e) => { e.stopPropagation(); reconciliar(b); }}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-600 text-white hover:bg-amber-700"
                          data-testid={`fin-corrigir-${b.academic_year}`}>
                    <Wrench className="w-3 h-3" /> Corrigir divergência
                  </button>
                </span>
              )
            )}
            <div className="text-right text-[11px] text-slate-500">
              {b.titulos_gerados
                ? <>Títulos: <b className="text-slate-800">{b.titulos?.quantidade}</b> · {b.titulos?.pagos} pago(s) · {b.titulos?.pendentes} pendente(s) · {b.titulos?.vencidos} vencido(s)<br />
                    <b className="text-slate-800">{money(b.titulos?.valor_total)}</b></>
                : <span className="text-amber-700 font-semibold">Títulos: ainda não gerados</span>}
            </div>
            <button type="button" onClick={() => setPlanDlg({ contract: b, item: null, coursePlans: b.planos_do_curso })}
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border border-violet-300 text-violet-700 hover:bg-violet-50"
                    data-testid={`fin-add-plano-${b.academic_year}`}>
              <PlusCircle className="w-3.5 h-3.5" /> Adicionar plano
            </button>
            <button type="button" onClick={() => baixarExtrato(b.academic_year)}
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
                    data-testid={`fin-extrato-${b.academic_year}`}>
              <FileDown className="w-3.5 h-3.5" /> Extrato PDF
            </button>
            <button type="button" onClick={() => setTitDlg({ contract: b, item: null })}
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
                    data-testid={`fin-ver-titulos-${b.academic_year}`}>
              <Receipt className="w-3.5 h-3.5" /> Ver títulos
            </button>
          </div>

          <div className="divide-y divide-slate-100">
            {b.planos.length === 0 && (
              <p className="px-4 py-3 text-[12px] text-slate-400">
                Nenhum plano contratado neste contrato. Use "Adicionar plano".
              </p>
            )}
            {b.planos.map(p => (
              <div key={p.key} className="px-4 py-2.5 flex items-center gap-3 flex-wrap" data-testid={`fin-plano-${b.academic_year}-${p.key}`}>
                <div className="flex-1 min-w-[180px]">
                  <p className="text-[13px] font-semibold text-slate-800 flex items-center gap-1.5">
                    {p.label || p.categoria}
                    {!p.ativo && <span className="text-[9.5px] font-bold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">encerrado</span>}
                  </p>
                  <p className="text-[11px] text-slate-500">
                    {money(p.valor_final)} × {p.parcelas}
                    {p.mes_inicial ? ` · início ${MESES[(p.mes_inicial || 1) - 1]}${p.ano_inicial ? `/${p.ano_inicial}` : ''}` : ''}
                    {p.dia_vencimento ? ` · venc. dia ${p.dia_vencimento}` : ''}
                    {p.desconto_tipo !== 'none' && p.desconto_valor ? ` · desconto ${p.desconto_tipo === 'percent' ? `${p.desconto_valor}%` : money(p.desconto_valor)}` : ''}
                    {` · total ${money(p.total_contratado)}`}
                  </p>
                  {p.observacao && <p className="text-[10.5px] text-slate-400">{p.observacao}</p>}
                </div>
                <div className="text-[11px] shrink-0" data-testid={`fin-plano-consist-${p.key}`}>
                  {p.previstas > 0 && !p.consistente ? (
                    <span className="inline-flex items-center gap-1 font-bold text-amber-700">
                      <AlertTriangle className="w-3 h-3" /> {p.gerados} de {p.previstas} títulos gerados
                    </span>
                  ) : p.titulos.quantidade > 0 ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700">
                      <CheckCircle2 className="w-3 h-3" /> {p.titulos.quantidade} título(s) · {p.titulos.pagos} pago(s)
                    </span>
                  ) : <span className="text-slate-400">sem títulos</span>}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button type="button" onClick={() => setTitDlg({ contract: b, item: p })}
                          className="text-[11px] px-2 py-1 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1"
                          data-testid={`fin-plano-titulos-${p.key}`}>
                    <Receipt className="w-3 h-3" /> Títulos
                  </button>
                  {b.editavel && (
                    <>
                      <button type="button" onClick={() => setPlanDlg({ contract: b, item: p, coursePlans: b.planos_do_curso })}
                              className="text-[11px] px-2 py-1 rounded-lg border border-violet-300 text-violet-700 hover:bg-violet-50 inline-flex items-center gap-1"
                              data-testid={`fin-plano-editar-${p.key}`}>
                        <Pencil className="w-3 h-3" /> Editar
                      </button>
                      <button type="button" onClick={() => excluir(b, p)}
                              className="text-[11px] px-2 py-1 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 inline-flex items-center gap-1"
                              data-testid={`fin-plano-excluir-${p.key}`}>
                        <Trash2 className="w-3 h-3" /> Excluir
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="px-4 py-2.5 bg-slate-50/60 border-t border-slate-100 flex items-center gap-2 flex-wrap">
            {!b.editavel && (
              <span className="text-[11px] text-amber-700 inline-flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Contrato histórico (sem plano vinculado) — valores vêm do snapshot/títulos reais.
              </span>
            )}
            {!b.titulos_gerados && (
              <a href="/v2/matriculas" target="_blank" rel="noreferrer"
                 className="text-[11px] font-semibold text-violet-700 hover:underline inline-flex items-center gap-1"
                 data-testid={`fin-ir-geracao-${b.academic_year}`}>
                Ir para geração de títulos <ChevronRight className="w-3 h-3" />
              </a>
            )}
            {b.planos_do_curso?.length > 0 && (
              <details className="w-full">
                <summary className="text-[11px] text-slate-500 cursor-pointer hover:text-slate-700">
                  Planos disponíveis do curso ({b.planos_do_curso.length}) — apenas para inclusão
                </summary>
                <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`fin-planos-curso-${b.academic_year}`}>
                  {b.planos_do_curso.map(cp => (
                    <span key={cp.id} className="text-[10.5px] px-2 py-1 rounded-lg bg-white border border-slate-200 text-slate-600">
                      {cp.name} · {money(cp.installment_value)} × {cp.num_installments || 1}
                    </span>
                  ))}
                </div>
              </details>
            )}
          </div>
        </div>
      ))}

      <PlanoDialog open={!!planDlg} onOpenChange={(v) => !v && setPlanDlg(null)}
                   contract={planDlg?.contract} item={planDlg?.item}
                   coursePlans={planDlg?.coursePlans} onDone={load} />
      <TitulosDialog open={!!titDlg} onOpenChange={(v) => !v && setTitDlg(null)}
                     contract={titDlg?.contract} item={titDlg?.item} />
    </div>
  );
};

export default FinanceiroContratosBlock;
